import type { ReactMonitoringData } from '@carboncopyco/types/desktop';
import { InputSearch } from '@carboncopyco/ui';
import { useEffect, useRef, useState } from 'react';
import { useTabs } from '../../store/context';

const REACT_MONITORING_INTERVAL = 3000;

const ReactMonitoring = () => {
  const [reactMonitoringData, setReactMonitoringData] =
    useState<ReactMonitoringData>();
  const [filteredReactMonitoringData, setFilteredReactMonitoringData] =
    useState<ReactMonitoringData>();
  const [searchValue, setSearchValue] = useState<string>('');
  const searchValueRef = useRef<string>(searchValue);

  const { globalTabs, activeGlobalTabId } = useTabs();

  const numberOfAllReactListeners =
    filteredReactMonitoringData?.listeners.reduce(
      (acc, listener) => acc + listener.listeners.count,
      0
    );

  const filterMonitoringData = (
    searchTerm: string,
    data: ReactMonitoringData
  ) => {
    if (!searchTerm) {
      setFilteredReactMonitoringData(data);
      return;
    }

    const filteredListeners = data?.listeners.filter((listener) =>
      listener.channel.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredReactMonitoringData({
      listeners: filteredListeners || [],
    });
  };

  const handleReactMonitoringData = () => {
    const listeners = window.dsDesktop.monitoring.ipcRenderer.allListeners();
    setReactMonitoringData({ listeners });
    filterMonitoringData(searchValueRef.current, {
      listeners,
    });
  };

  const handleOnChange = (value: string) => {
    filterMonitoringData(value, reactMonitoringData as ReactMonitoringData);
    setSearchValue(value);
    searchValueRef.current = value;
  };

  useEffect(() => {
    handleReactMonitoringData();

    const intervalId = setInterval(() => {
      handleReactMonitoringData();
    }, REACT_MONITORING_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      <details>
        <summary>
          Tabs (<strong>{globalTabs.length}</strong>)
        </summary>

        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                scope="col"
                className="px-6 py-3"
              >
                ID
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                TYPE
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                TITLE
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                PAYLOAD
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                OTHER PROPERTIES
              </th>
            </tr>
          </thead>
          <tbody>
            {globalTabs.map((tab) => (
              <tr
                key={tab.id}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
              >
                <th
                  scope="row"
                  className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {activeGlobalTabId === tab.id ? (
                    <span className="text-ds-danger">{tab.id} (is active)</span>
                  ) : (
                    tab.id
                  )}
                </th>
                <td className="px-6 py-4">{tab.type}</td>
                <td className="px-6 py-4">{tab.title || '/'}</td>
                <td className="px-6 py-4">
                  {tab.payload ? (
                    <details>
                      <summary>{Object.keys(tab.payload).join(', ')}</summary>

                      <div className="mt-2">
                        {tab.payload &&
                          Object.entries(tab.payload).map(([key, value]) => (
                            <p key={key}>
                              <span className="text-ds-marker-1">{key}</span>:{' '}
                              {JSON.stringify(value)},
                            </p>
                          ))}
                      </div>
                    </details>
                  ) : (
                    '/'
                  )}
                </td>
                <td className="px-6 py-4">
                  <details>
                    <summary>
                      {Object.keys(tab)
                        .filter(
                          (key) =>
                            !['id', 'type', 'title', 'payload'].includes(key)
                        )
                        .join(', ')}
                    </summary>

                    <div className="mt-2">
                      {Object.entries(tab)
                        .filter(
                          ([key]) =>
                            !['id', 'type', 'title', 'payload'].includes(key)
                        )
                        .map(([key, value]) => (
                          <p key={key}>
                            <span className="text-ds-marker-1">{key}</span>:{' '}
                            {JSON.stringify(value)},
                          </p>
                        ))}
                    </div>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      <hr className="my-6" />

      <details>
        <summary>
          Channels (
          <strong>{filteredReactMonitoringData?.listeners.length || 0}</strong>)
          and listeners (<strong>{numberOfAllReactListeners}</strong>)
        </summary>

        <div className="flex my-4">
          <InputSearch
            placeholder="Search..."
            value={searchValue}
            onChange={(value) =>
              typeof value === 'string' && handleOnChange(value)
            }
          />
        </div>
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Channel ({filteredReactMonitoringData?.listeners.length || 0})
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Number of listeners ({numberOfAllReactListeners})
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredReactMonitoringData?.listeners.map((listener) => (
              <tr
                key={listener.channel}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
              >
                <th
                  scope="row"
                  className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {listener.channel}
                </th>
                <td className="px-6 py-4">
                  <p>
                    {listener.listeners.count || 0} (handler names -{' '}
                    {listener.listeners.names.map((name, index) => (
                      <span key={`${name}-${index + 1}`}>
                        {name || 'anonymous'}
                        {index !== listener.listeners.names.length - 1
                          ? ', '
                          : ''}
                      </span>
                    ))}
                    )
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
};

export default ReactMonitoring;
